import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import useMediaQuery from "@/hooks/useMediaQuery";

/*
 * TtkBand · tech-spec scroll section.
 * The sketch is fixed in the left column. On the right, seven spec rows
 * scroll past. Each active row draws a leader line ending in a dot that
 * touches the labelled part of the sketch. Leader lines are drawn on a
 * container-wide SVG using JS-measured pixel positions so they always
 * connect the sketch anchor to the active spec card, regardless of the
 * grid gap or viewport width.
 */
const SPECS = [
  {
    n: "01",
    tag: "SENSOR",
    title: "Maxim medical-grade PPG",
    body: "The optical heart pulse the twin lives on. Same sensor family used in clinical wearables.",
    target: "body",
  },
  {
    n: "02",
    tag: "SENSOR",
    title: "Bosch 6D accelerometer",
    body: "Six axes of movement, so a sprint reads different from a shrug and the twin knows the difference.",
    target: "body",
  },
  {
    n: "03",
    tag: "POWER",
    title: "10-day battery life",
    body: "Charge it on a Sunday. The twin does not lose signal on a Wednesday.",
    target: "body",
  },
  {
    n: "04",
    tag: "DESIGN",
    title: "Displayless, distraction-free body",
    body: "No screen means nothing to check. Your attention stays on the life the twin is watching.",
    target: "top",
  },
  {
    n: "05",
    tag: "DESIGN",
    title: "Ultralight body under 13 grams",
    body: "Light enough to forget. Worn round the clock is how the twin keeps up.",
    target: "body",
  },
  {
    n: "06",
    tag: "DESIGN",
    title: "Two straps · Swag and Sweat",
    body: "A stylish Swag strap for the day. A sweat-wicking Sweat strap for when the twin is watching you push.",
    target: "strap",
  },
  {
    n: "07",
    tag: "CONNECTION",
    title: "Nordic BLE",
    body: "A quiet, low-power link so signals reach the app without the band ever getting warm on your wrist.",
    target: "body",
  },
];

const N = SPECS.length;

// Anchor coordinates as fractions of the sketch's DRAWN image area
// (not the container, the actual visible image after object-contain).
const ANCHORS = {
  body: { x: 0.22, y: 0.55 },
  top: { x: 0.21, y: 0.44 },
  strap: { x: 0.44, y: 0.78 },
};

const Header = () => (
  <>
    <p className="font-mono text-[11px] tracking-[0.28em] text-ash">
      04 · THE SENSOR · TRIKARMA BAND
    </p>
    <h2
      data-testid="ttk-band-headline"
      className="mt-4 max-w-md font-display text-3xl font-light leading-tight text-white md:text-4xl"
    >
      Your body. <span className="text-signal">Continuously</span> understood.
    </h2>
  </>
);

const SpecCard = ({ s }) => (
  <div
    data-testid={`ttk-band-spec-${s.n}`}
    className="rounded-md border border-signal/60 bg-signal/5 p-5 md:p-6"
  >
    <div className="flex items-baseline justify-between gap-4">
      <p className="font-mono text-[10px] tracking-[0.22em] text-signal">
        SPEC {s.n} · {s.tag}
      </p>
      <span className="font-mono text-[9px] tracking-[0.2em] text-signal">
        ● LIVE
      </span>
    </div>
    <p className="mt-3 font-display text-lg font-light leading-tight text-white md:text-xl">
      {s.title}
    </p>
    <p className="mt-2 text-sm leading-relaxed text-fog">{s.body}</p>
  </div>
);

/* -------------------------------------------------------------------- */
/*  Sub-components · each owns its own hooks                              */
/* -------------------------------------------------------------------- */
const AnimatedSpecRow = ({ p, i, s, cardRef }) => {
  const start = i / N;
  const end = (i + 1) / N;
  const y = useTransform(
    p,
    [start - 0.012, start + 0.012, end - 0.012, end + 0.012],
    [40, 0, 0, -40]
  );
  const opacity = useTransform(
    p,
    [start - 0.012, start + 0.012, end - 0.012, end + 0.012],
    [0, 1, 1, 0]
  );
  return (
    <motion.div
      ref={cardRef}
      style={{ y, opacity }}
      className="absolute inset-x-0 top-0"
    >
      <SpecCard s={s} />
    </motion.div>
  );
};

const ProgressPip = ({ p, i }) => {
  const start = i / N;
  const end = (i + 1) / N;
  const opacity = useTransform(
    p,
    [start - 0.008, start + 0.012, end - 0.012, end + 0.008],
    [0.2, 1, 1, 0.2]
  );
  return <motion.span style={{ opacity }} className="h-px flex-1 bg-signal" />;
};

const LeaderPath = ({ p, i, s, geom }) => {
  const start = i / N;
  const end = (i + 1) / N;
  const opacity = useTransform(
    p,
    [start - 0.008, start + 0.012, end - 0.012, end + 0.008],
    [0, 1, 1, 0]
  );
  if (!geom) return null;
  const anchor = geom.anchors[s.target];
  const specX = geom.specLeft;
  const specY = geom.specCenterY;
  // Path: from anchor dot → out along +x to midX → down/up to spec card Y → into spec card
  const midX = anchor.x + (specX - anchor.x) * 0.55;
  const d = `M ${anchor.x} ${anchor.y} L ${midX} ${anchor.y} L ${midX} ${specY} L ${specX} ${specY}`;
  return (
    <motion.g style={{ opacity }}>
      <path
        d={d}
        stroke="#D2378B"
        strokeOpacity="0.85"
        strokeWidth="1.25"
        fill="none"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      {/* Anchor dot on the sketch */}
      <circle cx={anchor.x} cy={anchor.y} r="4" fill="#D2378B" />
      <circle cx={anchor.x} cy={anchor.y} r="9" fill="#D2378B" opacity="0.22" />
      {/* Card side dot */}
      <circle cx={specX} cy={specY} r="3.5" fill="#D2378B" />
    </motion.g>
  );
};

/* -------------------------------------------------------------------- */
/*  Desktop · pinned                                                     */
/* -------------------------------------------------------------------- */
const TtkBandDesktop = () => {
  const ref = useRef(null);
  const containerRef = useRef(null);
  const sketchImgRef = useRef(null);
  const specSlotRef = useRef(null);
  const firstCardRef = useRef(null);
  const [geom, setGeom] = useState(null);
  const [size, setSize] = useState({ w: 0, h: 0 });

  const { scrollYProgress: p } = useScroll({
    target: ref,
    offset: ["start start", "end end"],
  });

  // Measure sketch anchor points + spec slot in container coords
  useLayoutEffect(() => {
    const measure = () => {
      const container = containerRef.current;
      const sketch = sketchImgRef.current;
      const slot = specSlotRef.current;
      const card = firstCardRef.current;
      if (!container || !sketch || !slot || !card) return;
      const cRect = container.getBoundingClientRect();
      const sRect = sketch.getBoundingClientRect();
      const slotRect = slot.getBoundingClientRect();
      const cardRect = card.getBoundingClientRect();

      // Compute the actual DRAWN image rect inside the img element for
      // object-contain of a 1:1 source.
      const imgW = sRect.width;
      const imgH = sRect.height;
      const imgAspect = 1;
      const drawnW = imgAspect >= imgW / imgH ? imgW : imgH * imgAspect;
      const drawnH = imgAspect >= imgW / imgH ? imgW / imgAspect : imgH;
      const drawnX = sRect.left + (imgW - drawnW) / 2 - cRect.left;
      const drawnY = sRect.top + (imgH - drawnH) / 2 - cRect.top;

      const anchors = {};
      for (const key of Object.keys(ANCHORS)) {
        anchors[key] = {
          x: drawnX + ANCHORS[key].x * drawnW,
          y: drawnY + ANCHORS[key].y * drawnH,
        };
      }

      // Active spec cards are absolutely positioned at top-0 of the slot,
      // so their vertical centre is slot.top + card.height / 2.
      const activeCardCenterY =
        slotRect.top + cardRect.height / 2 - cRect.top;

      setGeom({
        anchors,
        specLeft: slotRect.left - cRect.left,
        specCenterY: activeCardCenterY,
      });
      setSize({ w: cRect.width, h: cRect.height });
    };

    measure();
    const ro = new ResizeObserver(measure);
    if (containerRef.current) ro.observe(containerRef.current);
    if (sketchImgRef.current) ro.observe(sketchImgRef.current);
    if (firstCardRef.current) ro.observe(firstCardRef.current);
    window.addEventListener("resize", measure);
    return () => {
      ro.disconnect();
      window.removeEventListener("resize", measure);
    };
  }, []);

  return (
    <section
      ref={ref}
      data-testid="ttk-band-section"
      className="relative h-[520vh]"
    >
      <div className="sticky top-0 flex h-screen items-center overflow-hidden">
        <p
          aria-hidden="true"
          className="pointer-events-none absolute -bottom-6 left-0 select-none whitespace-nowrap font-display text-[16vw] font-extralight leading-none text-white/[0.035]"
        >
          SENSED · NOT GUESSED
        </p>

        <div
          ref={containerRef}
          className="relative mx-auto grid w-full max-w-[1600px] grid-cols-12 items-center gap-6 px-6 md:px-12"
        >
          {/* LEFT · sketch */}
          <div className="relative col-span-7 h-[78vh]">
            <img
              ref={sketchImgRef}
              src="/assets/band_sketch.jpg"
              alt="TriKarma Band engineering wireframe"
              data-testid="ttk-band-sketch"
              className="absolute inset-0 h-full w-full object-contain"
              style={{ mixBlendMode: "lighten" }}
            />
          </div>

          {/* RIGHT · header + spec list */}
          <div className="col-span-5 col-start-8 flex h-[80vh] flex-col">
            <Header />

            <div className="mt-8 flex-1 overflow-hidden">
              <div ref={specSlotRef} className="relative h-full">
                {SPECS.map((s, i) => (
                  <AnimatedSpecRow
                    key={s.n}
                    p={p}
                    i={i}
                    s={s}
                    cardRef={i === 0 ? firstCardRef : undefined}
                  />
                ))}
              </div>
            </div>

            <div className="mt-6 flex items-center gap-2">
              {SPECS.map((s, i) => (
                <ProgressPip key={s.n} p={p} i={i} />
              ))}
              <span className="ml-3 font-mono text-[9px] tracking-[0.2em] text-ash">
                07 / 07
              </span>
            </div>
          </div>

          {/* Container-wide SVG overlay for leader lines · anchors on sketch → active spec */}
          {geom && (
            <svg
              className="pointer-events-none absolute inset-0 h-full w-full"
              width={size.w}
              height={size.h}
              viewBox={`0 0 ${size.w} ${size.h}`}
              aria-hidden="true"
            >
              {SPECS.map((s, i) => (
                <LeaderPath key={s.n} p={p} i={i} s={s} geom={geom} />
              ))}
            </svg>
          )}
        </div>
      </div>
    </section>
  );
};

const TtkBandMobile = () => (
  <section
    data-testid="ttk-band-section-mobile"
    className="relative px-6 py-20"
  >
    <Header />
    <div className="mt-8 aspect-square w-full">
      <img
        src="/assets/band_sketch.jpg"
        alt="TriKarma Band engineering wireframe"
        className="h-full w-full object-contain"
        style={{ mixBlendMode: "lighten" }}
      />
    </div>
    <div className="mt-8 space-y-4">
      {SPECS.map((s) => (
        <SpecCard key={s.n} s={s} />
      ))}
    </div>
  </section>
);

const TtkBand = () => {
  const pinned = useMediaQuery("(min-width: 1024px) and (min-height: 620px)");
  return pinned ? <TtkBandDesktop /> : <TtkBandMobile />;
};

export default TtkBand;
